import { useMedia } from 'tamagui';

export type BreakpointKey = 'sm' | 'md' | 'lg' | 'xl';

export interface BreakpointInfo {
  /** True at viewport widths < 768px. Default mobile target. */
  sm: boolean;
  /** True at viewport widths >= 768px. */
  md: boolean;
  /** True at viewport widths >= 1024px. */
  lg: boolean;
  /** True at viewport widths >= 1280px. */
  xl: boolean;
  /**
   * The highest-rank matching breakpoint as a string union — saves
   * the chained boolean check pattern when callers want to switch on
   * size.
   *
   *   const { current } = useBreakpoint();
   *   if (current === 'sm') return <Phone />;
   *   if (current === 'md') return <Tablet />;
   */
  current: BreakpointKey;
}

/**
 * Single responsive API per ADR-0078. Wraps Tamagui's `useMedia()` so
 * the same hook backs both Tamagui responsive props (`$md={…}`) and
 * runtime layout decisions in app code.
 *
 * Replaces ad-hoc `useWindowDimensions().width < 768` patterns. The
 * Phase 6 ESLint rule `no-window-dimensions-for-layout` forbids those
 * outside an allow-list; this hook is the canonical replacement.
 *
 * Returns `sm` true on phones (`< 768px`); `md` true at tablet and up;
 * `lg` at small-desktop; `xl` at desktop. `current` resolves to the
 * highest-rank matching key so callers can switch on a string.
 */
export function useBreakpoint(): BreakpointInfo {
  const media = useMedia();
  // Tamagui's useMedia returns booleans for each breakpoint key. The
  // sm key uses maxWidth (so it's exclusive of md) per
  // tamagui.config.ts. md/lg/xl are minWidth — overlapping by design.
  const sm = !media.md;
  const md = !!media.md;
  const lg = !!media.lg;
  const xl = !!media.xl;

  const current: BreakpointKey = xl ? 'xl' : lg ? 'lg' : md ? 'md' : 'sm';

  return { sm, md, lg, xl, current };
}
